
import { useCallback, useRef, useState } from 'react';
import { Loader2, Search, Smile } from 'lucide-react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import {
  CopyTooltip,
  GsImage,
  chatSmileyCode,
  copyText,
  labelBadgeLight,
  smileyTileBtnLight,
  type PowerPawn,
} from '@/components/help-center/latest-power-shared';
import { normalizeApiPawns } from '@/lib/powers-data';
import { smileyStripUrl } from '@/lib/xat-smilie';
import { XAT_API } from '@/services/api-paths';
import { cn } from '@/lib/utils';

const SMILEY_TEST_BG = 'https://xatimg.com/image/UmpsHX74cXbn.png';

type PowerGalleryData = {
  name: string;
  slug: string;
  smilies: string[];
  pawns: PowerPawn[];
};

async function fetchPowerGallery(slug: string): Promise<PowerGalleryData> {
  const key = slug.trim().toLowerCase();
  if (!key) throw new Error('Indique o nome ou slug do power.');

  const res = await fetch(XAT_API.powerinfo(key), { cache: 'no-store' });
  if (!res.ok) throw new Error('Power não encontrado na API.');

  const data = await res.json();
  const raw =
    data?.code === 200 && data.power
      ? data.power
      : data?.code === 200 && Array.isArray(data.powers) && data.powers.length > 0
        ? data.powers[0]
        : null;

  if (!raw || typeof raw !== 'object') throw new Error('Resposta inválida da API.');

  return {
    name: typeof raw.name === 'string' ? raw.name : key,
    slug: key,
    smilies: Array.isArray(raw.smilies) ? raw.smilies.map(String).filter(Boolean) : [],
    pawns: normalizeApiPawns(raw as Record<string, unknown>),
  };
}

const PREVIEW_SPRITE_PX = 160;

function TestSmileyTile({
  code,
  copiedKey,
  onCopy,
}: {
  code: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const key = `sm-${code}`;
  const chat = chatSmileyCode(code);
  const isCopied = copiedKey === key;

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onCopy(key, chat)}
      title={`Copiar ${chat}`}
      className={cn(smileyTileBtnLight, 'cursor-pointer select-none')}
    >
      <CopyTooltip visible={isCopied} side="bottom" anchorRef={ref} />
      <GsImage
        code={code}
        sizePx={PREVIEW_SPRITE_PX}
        className="flex h-[160px] w-[160px] items-center justify-center"
        imgClassName="h-[160px] w-[160px] object-contain"
      />
      <span className={cn(labelBadgeLight, 'font-mono text-[10px]')}>{chat}</span>
    </button>
  );
}

function TestPawnTile({
  pawn,
  index,
  copiedKey,
  onCopy,
}: {
  pawn: PowerPawn;
  index: number;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const key = `pawn-${index}-${pawn.hat}`;
  const isCopied = copiedKey === key;

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onCopy(key, pawn.hat)}
      title={`Copiar ${pawn.hat}`}
      className={cn(smileyTileBtnLight, 'cursor-pointer select-none')}
    >
      <CopyTooltip visible={isCopied} side="bottom" anchorRef={ref} />
      <GsImage
        code={pawn.name}
        sizePx={PREVIEW_SPRITE_PX}
        className="flex h-[160px] w-[160px] items-center justify-center"
        imgClassName="h-[160px] w-[160px] object-contain object-bottom"
      />
      <span className={cn(labelBadgeLight, 'font-mono text-[10px]')}>{pawn.hat}</span>
    </button>
  );
}

function GalleryPanel({
  power,
  copiedKey,
  onCopy,
}: {
  power: PowerGalleryData;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
}) {
  const hasSmilies = power.smilies.length > 0;
  const hasPawns = power.pawns.length > 0;

  if (!hasSmilies && !hasPawns) {
    return (
      <p className="rounded-xl border border-dashed border-white/20 bg-black/20 px-4 py-10 text-center font-inter text-sm text-white/60">
        A API não devolveu smilies nem peões para este power.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {hasSmilies ? (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Smile className="h-4 w-4 text-white/80" aria-hidden />
            <h3 className="font-poppins text-sm font-bold text-white">
              Smilies
              <span className="ml-2 font-normal text-white/50">({power.smilies.length})</span>
            </h3>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(184px,1fr))] gap-3 sm:gap-4">
            {power.smilies.map((code, index) => (
              <TestSmileyTile
                key={`${code}-${index}`}
                code={code}
                copiedKey={copiedKey}
                onCopy={onCopy}
              />
            ))}
          </div>
        </div>
      ) : null}

      {hasPawns ? (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <img
              src={smileyStripUrl('p')}
              alt=""
              className="h-4 w-4 object-contain"
              aria-hidden
            />
            <h3 className="font-poppins text-sm font-bold text-white">
              Peões
              <span className="ml-2 font-normal text-white/50">({power.pawns.length})</span>
            </h3>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(184px,1fr))] gap-3 sm:gap-4">
            {power.pawns.map((pawn, index) => (
              <TestPawnTile
                key={`${pawn.hat}-${pawn.name}-${index}`}
                pawn={pawn}
                index={index}
                copiedKey={copiedKey}
                onCopy={onCopy}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function SmileyTestPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [power, setPower] = useState<PowerGalleryData | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback(async (key: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1800);
    }
  }, []);

  const handleSearch = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const slug = query.trim();
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchPowerGallery(slug);
      setPower(result);
    } catch (err) {
      setPower(null);
      setError(err instanceof Error ? err.message : 'Erro ao carregar o power.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#030023]">
      <Navbar />

      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-8 px-6 py-12 md:py-16">
        <header className="text-center">
          <p className="font-poppins text-xs font-semibold uppercase tracking-[0.2em] text-sky-300/70">
            Ferramenta interna
          </p>
          <h1 className="mt-2 font-poppins text-3xl font-bold text-white md:text-4xl">Smiley Test</h1>
          <p className="mx-auto mt-3 max-w-xl font-inter text-sm text-blue-100/65">
            Introduza o nome ou slug do power — os smilies e peões são carregados em tempo real pela API
            xatBlog.
          </p>
        </header>

        <form
          onSubmit={handleSearch}
          className="mx-auto flex w-full max-w-md items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] p-2 shadow-lg shadow-black/30 backdrop-blur-sm"
        >
          <Search className="ml-2 h-4 w-4 shrink-0 text-sky-300/70" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ex.: bwglow, nameglow, stella…"
            className="min-w-0 flex-1 bg-transparent py-2.5 font-inter text-sm text-white placeholder:text-blue-200/35 focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5',
              'font-poppins text-xs font-bold uppercase tracking-wide text-white',
              'bg-gradient-to-r from-sky-600 to-cyan-500',
              'transition-opacity hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Carregar
          </button>
        </form>

        <div
          className="relative overflow-hidden rounded-[28px] border border-[#0f1e70]/80 shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
          style={{
            backgroundImage: `url('${SMILEY_TEST_BG}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-[#030023]/25" aria-hidden />

          <div className="relative px-5 py-8 md:px-8 md:py-10">
            {power ? (
              <div className="mb-6 text-center">
                <p className="font-poppins text-lg font-bold capitalize text-white">{power.name}</p>
                <p className="mt-1 font-mono text-xs text-white/50">/powers/{power.slug}</p>
              </div>
            ) : null}

            {error ? (
              <p className="rounded-xl border border-rose-400/30 bg-rose-950/40 px-4 py-6 text-center font-inter text-sm text-rose-100">
                {error}
              </p>
            ) : null}

            {!error && !power && !loading ? (
              <p className="rounded-xl border border-dashed border-white/20 bg-black/15 px-4 py-12 text-center font-inter text-sm text-white/55">
                Pesquise um power para ver a grelha de smilies e peões.
              </p>
            ) : null}

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 font-inter text-sm text-white/70">
                <Loader2 className="h-5 w-5 animate-spin text-sky-300" aria-hidden />
                A consultar a API…
              </div>
            ) : null}

            {power && !loading ? (
              <GalleryPanel power={power} copiedKey={copiedKey} onCopy={handleCopy} />
            ) : null}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
